import React from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { formatTime } from '../../utils/dashboardDates';
import { procedureLabels } from '../../../appointments/v2/utils/appointmentUtils';
import { getInitials } from '../shared/PatientItem';
import IconAction from '../shared/IconAction';
import NowStepsBadge from './NowStepsBadge';
import styles from './NowPanel.module.css';

const MAX_ROWS = 4;

// Status curto embaixo do horário, para o profissional caber inteiro na linha
const SHORT_STATUS = {
  doing: { label: 'Atendendo', className: 'statusDoing' },
  waiting: { label: 'Aguardando', className: 'statusWaiting' },
  scheduled: { label: 'A confirmar', className: 'statusPending' },
};

function NowRow({ appointment, showResource, canAttend, onStepChecksChange }) {
  const navigate = useNavigate();
  const { CompanyConfig, GetPatientById, GetResourceById } = DataProviderInstance();

  const name = GetPatientById(appointment.patient)?.name || 'Paciente';
  const resource = GetResourceById(appointment.resource)?.Label || '';
  const procedure = procedureLabels(appointment, CompanyConfig?.Procedures);
  const status = SHORT_STATUS[appointment.status];
  const detail = showResource ? resource : procedure;

  return (
    <li className={styles.row}>
      <span className={styles.when}>
        <span className={styles.time}>{formatTime(appointment.date)}</span>
        {status && <span className={styles[status.className]}>{status.label}</span>}
      </span>
      <span className={`${styles.avatar} ${appointment.status === 'doing' ? styles.avatarDoing : ''}`} aria-hidden="true">
        {getInitials(name)}
      </span>
      <div className={styles.info}>
        <span className={styles.name}>{name}</span>
        {detail && <span className={styles.meta}>{detail}</span>}
      </div>
      <NowStepsBadge
        appointment={appointment}
        onChecksChange={(stepChecks) => onStepChecksChange(appointment.id, stepChecks)}
      />
      {/* Sem a marcação "Profissional de saúde" a pessoa não acessa a tela de atendimento */}
      {canAttend && (
        <IconAction title="Abrir atendimento" onClick={() => navigate(`/appointment/${appointment.id}`)}>
          <ArrowForwardIcon style={{ fontSize: 16 }} />
        </IconAction>
      )}
    </li>
  );
}

/**
 * Quem está na clínica agora (vários profissionais podem atender ao mesmo
 * tempo) e, no espaço que sobra, os próximos pacientes.
 */
function NowPanel({ live, upcoming, hasAppointments, showResource, canAttend, onStepChecksChange }) {
  const navigate = useNavigate();

  if (live.length === 0 && upcoming.length === 0) {
    return (
      <div className={`${styles.panel} ${styles.empty}`}>
        <span className={styles.emptyIcon}>
          <EventAvailableOutlinedIcon style={{ fontSize: 22 }} />
        </span>
        <span className={styles.emptyTitle}>
          {hasAppointments ? 'Sem mais pacientes hoje' : 'Agenda livre hoje'}
        </span>
        <span className={styles.emptyText}>
          {hasAppointments ? 'Os próximos horários aparecem aqui.' : 'Quando houver consultas, os pacientes do dia aparecem aqui.'}
        </span>
      </div>
    );
  }

  const liveShown = live.slice(0, MAX_ROWS);
  const nextShown = upcoming.slice(0, MAX_ROWS - liveShown.length);
  const hidden = live.length + upcoming.length - liveShown.length - nextShown.length;
  const rowProps = { showResource, canAttend, onStepChecksChange };

  return (
    <div className={styles.panel}>
      <header className={styles.header}>
        <h2 className={styles.title}>{live.length > 0 ? 'Agora na clínica' : 'Próximos pacientes'}</h2>
        <p className={styles.subtitle}>
          {live.length > 0 && <span className={styles.liveDot} aria-hidden="true" />}
          {live.length > 0
            ? `${live.length} ${live.length === 1 ? 'paciente' : 'pacientes'} em atendimento ou aguardando`
            : 'Quem chega a seguir'}
        </p>
      </header>

      <ul className={styles.list}>
        {liveShown.map((a) => <NowRow key={a.id} appointment={a} {...rowProps} />)}
      </ul>

      {nextShown.length > 0 && (
        <>
          {liveShown.length > 0 && <span className={styles.subLabel}>A seguir</span>}
          <ul className={styles.list}>
            {nextShown.map((a) => <NowRow key={a.id} appointment={a} {...rowProps} />)}
          </ul>
        </>
      )}

      {hidden > 0 && (
        <button type="button" className={styles.more} onClick={() => navigate('/schedule/appointments')}>
          +{hidden} na agenda de hoje
          <ArrowForwardIcon style={{ fontSize: 14 }} />
        </button>
      )}
    </div>
  );
}

export default NowPanel;
